/**
 * Top Earning Creators — relational report.
 *
 * Window-based deep cut: earnings within range split across all three income
 * streams — Calls (NET call earnings after commission, from the youtuber-earning
 * ledger — NOT the earnings table, which over-counts), Brand (campaign payouts),
 * and Referral (referral bonuses Fancall pays for invites) — plus actual
 * withdrawals and current wallet balance. Total earned = Calls + Brand +
 * Referral, which reconciles with Withdrawn + Wallet bal. "Paid calls" is the
 * number of completed paid calls. Mirrors the Top Spending Fans report's UX.
 */
import React, { useState } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useTopEarningCreators } from "./useInsights.v2";

function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function yearAgoStr() {
  const d = new Date(); d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

export function TopEarningCreatorsReport() {
  const [filters, setFilters] = useState({ from: yearAgoStr(), to: todayStr(), limit: 50, minEarnings: 0 });
  const { data, loading, error } = useTopEarningCreators(filters);

  const onExport = () => {
    if (!data?.rows) return;
    const cols = ["name", "totalEarned", "callEarned", "brandEarned", "referralEarned", "brandSharePct", "withdrawn", "walletBalance", "earningEntries"];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = `# Top Earning Creators · ${filters.from} → ${filters.to}\n` + cols.join(",");
    const lines = data.rows.map(r => cols.map(c => escape(r[c])).join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `top-earning-creators-${filters.from}-to-${filters.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div style={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 14, padding: 16, fontFamily: T.font.sans }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text.primary }}>Top Earning Creators</div>
          <div style={{ fontSize: 12, color: T.text.muted, marginTop: 2 }}>
            Window: {filters.from} → {filters.to} · {data?.totals?.rowCount ?? "—"} creators · total Rs {formatINR(data?.totals?.sumEarned || 0)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 11, color: T.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>From</label>
          <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                 style={{ padding: "6px 8px", border: `1px solid ${T.border.subtle}`, borderRadius: 6, fontSize: 12, fontFamily: T.font.sans }} />
          <label style={{ fontSize: 11, color: T.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>To</label>
          <input type="date" value={filters.to}   onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                 style={{ padding: "6px 8px", border: `1px solid ${T.border.subtle}`, borderRadius: 6, fontSize: 12, fontFamily: T.font.sans }} />
          <button type="button" onClick={onExport} disabled={!data?.rows?.length}
                  style={{ padding: "6px 14px", background: T.brand.base, color: "#FFFFFF", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: data?.rows?.length ? "pointer" : "not-allowed", opacity: data?.rows?.length ? 1 : 0.5 }}>Export CSV</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", background: T.status.warnBg, border: `1px solid ${T.status.warn}`, borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
          <strong>Could not load report:</strong> {error}
        </div>
      )}
      {loading && !data && (
        <div style={{ padding: 20, textAlign: "center", color: T.text.muted, fontSize: 13 }}>Loading…</div>
      )}

      {data?.rows && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font.sans, fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border.subtle}`, fontSize: 10, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <th style={{ textAlign: "left",  padding: "8px", fontWeight: 700 }}>#</th>
                <th style={{ textAlign: "left",  padding: "8px", fontWeight: 700 }}>Creator</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Total earned</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Calls</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Brand</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Referral</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Brand %</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Withdrawn</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Wallet bal.</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Paid calls</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 20, color: T.text.muted }}>No earnings in window.</td></tr>
              ) : data.rows.map((r, i) => (
                <tr key={r.celebrityId} style={{ borderBottom: i < data.rows.length - 1 ? `1px solid ${T.border.subtle}` : "none" }}>
                  <td style={{ padding: "8px", color: T.text.muted, fontWeight: 700 }}>{i + 1}</td>
                  <td style={{ padding: "8px", fontWeight: 600, color: T.text.primary }}>{r.name}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontWeight: 700, color: T.text.primary }}>{formatINR(r.totalEarned)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.callEarned)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.brandEarned)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.referralEarned)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: r.brandSharePct >= 30 ? "#7C4DFF" : T.text.muted, fontWeight: r.brandSharePct >= 30 ? 700 : 500 }}>{r.brandSharePct.toFixed(1)}%</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.withdrawn)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.walletBalance)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.muted }}>{formatNum(r.earningEntries)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
